import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { analytics, functionsInstance } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import TrialPromptPopup from './TrialPromptPopup';

const HOLIDAY_DATE = new Date('2026-05-11');
const HOLIDAY_TAG  = 'mothers-day-2026';

function getDaysAway() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((HOLIDAY_DATE - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "is TODAY! 🎉";
  if (diff === 1) return "is TOMORROW ⏰";
  if (diff < 0)  return "has passed — but it's never too early for next year!";
  return `is in ${diff} days ⏰`;
}

const MothersDay2026 = () => {
  const hasLoggedRead  = useRef(false);
  const [email, setEmail]               = useState('');
  const [gdprChecked, setGdprChecked]   = useState(false);
  const [submitState, setSubmitState]   = useState('idle');
  const [errorMsg, setErrorMsg]         = useState('');
  const [showPopup, setShowPopup]       = useState(false);
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  const daysAway = getDaysAway();

  useEffect(() => {
    document.title = "Best TPT Keywords for Mother's Day 2026 | Mr Joel's Seller Tools";
    window.scrollTo(0, 0);
    logEvent(analytics, 'page_view', {
      page_title: "Mother's Day Keyword Report Signup",
      page_path: '/keywords/mothers-day-2026'
    });
    const handleScroll = () => {
      if (window.scrollY > 300 && !hasLoggedRead.current) {
        logEvent(analytics, 'mothers_day_report_read_content');
        hasLoggedRead.current = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!gdprChecked) return;
    setSubmitState('loading');
    setErrorMsg('');
    try {
      logEvent(analytics, 'seasonal_report_signup_submit', { tag: HOLIDAY_TAG });
      const subscribeToSeasonalReport = httpsCallable(functionsInstance, 'subscribeToSeasonalReport');
      const result = await subscribeToSeasonalReport({ email, tag: HOLIDAY_TAG });
      const { userType } = result.data;
      if (userType === 'existing') {
        setSubmitState('success-existing');
        logEvent(analytics, 'seasonal_report_existing_user', { tag: HOLIDAY_TAG });
      } else {
        setSubmitState('success-new');
        logEvent(analytics, 'seasonal_report_new_subscriber', { tag: HOLIDAY_TAG });
        // Show trial prompt popup after 5 seconds for new mailing list signups
        setTimeout(() => setShowTrialPopup(true), 5000);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setErrorMsg('Something went wrong — please try again or email info@mrjoelstptgenerator.com');
      setSubmitState('error');
    }
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    const input = document.getElementById('md-email');
    if (input) { input.focus(); input.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  };

  return (
    <div className="md-wrapper">

      {/* Trial Prompt Popup — shown 5s after new mailing list signup */}
      {showTrialPopup && (
        <TrialPromptPopup
          onClose={() => setShowTrialPopup(false)}
          accentColor="#db2777"
          accentDark="#9d174d"
          accentLight="#fdf2f8"
          accentBorder="#f9a8d4"
        />
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=DM+Sans:ital,wght@0,400;0,500;1,400&display=swap');

        .md-wrapper {
          --pink:       #db2777;
          --pink-dark:  #9d174d;
          --pink-light: #fdf2f8;
          --pink-border:#f9a8d4;
          --blue:       #1a56db;
          --blue-dark:  #1e40af;
          --blue-light: #eff6ff;
          --gold:       #fbbf24;
          --gold-bg:    #fffbeb;
          --text:       #0f172a;
          --muted:      #64748b;
          --border:     #e2e8f0;
          --bg:         #f8fafc;
          font-family: 'DM Sans', sans-serif;
          background: var(--bg);
          color: var(--text);
          min-height: 100vh;
          overflow-x: hidden;
        }

        .md-nav {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 32px; background: white;
          border-bottom: 1px solid var(--border);
          position: sticky; top: 0; z-index: 100;
        }
        .md-nav-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
        .md-nav-mark {
          width: 36px; height: 36px;
          background: linear-gradient(135deg, var(--pink), var(--pink-dark));
          border-radius: 9px; display: flex; align-items: center; justify-content: center;
          font-family: 'Sora', sans-serif; font-weight: 900; font-size: 14px; color: white;
        }
        .md-nav-text { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 14px; color: var(--text); }
        .md-nav-sub  { font-size: 11px; color: var(--muted); }
        .md-nav-cta {
          font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 600;
          color: var(--pink); text-decoration: none;
          border: 1px solid var(--pink-border); padding: 7px 16px; border-radius: 8px; transition: background 0.15s;
        }
        .md-nav-cta:hover { background: var(--pink-light); }

        .md-urgency {
          background: linear-gradient(135deg, var(--pink-dark), var(--pink));
          color: white; text-align: center; padding: 10px 24px; font-size: 14px; font-weight: 600;
        }

        .md-hero {
          max-width: 1080px; margin: 0 auto;
          padding: 60px 24px 40px; text-align: center;
        }
        .md-badge {
          display: inline-flex; align-items: center; gap: 7px;
          background: var(--pink-light); border: 1px solid var(--pink-border);
          color: var(--pink-dark); font-size: 12px; font-weight: 700;
          padding: 6px 16px; border-radius: 20px; margin-bottom: 24px;
          letter-spacing: 0.03em; text-transform: uppercase;
        }
        .md-badge-dot { width: 6px; height: 6px; background: var(--pink); border-radius: 50%; animation: pulse 2s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }

        .md-wrapper h1, .sora { font-family: 'Sora', sans-serif; }
        .md-title {
          font-weight: 900; font-size: clamp(30px, 5vw, 54px);
          line-height: 1.1; letter-spacing: -0.03em; color: var(--text); margin-bottom: 20px;
        }
        .md-title .pink-accent {
          background: linear-gradient(135deg, var(--pink), var(--pink-dark));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .md-title .gold { color: #d97706; -webkit-text-fill-color: #d97706; }
        .md-sub { font-size: 18px; color: var(--muted); line-height: 1.7; margin-bottom: 40px; max-width: 800px; margin-left: auto; margin-right: auto; }
        .md-sub strong { color: var(--text); }

        .md-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; max-width: 800px; margin: 0 auto; }
        .md-stat { background: white; border: 1px solid var(--border); border-radius: 16px; padding: 20px; text-align: center; }
        .md-stat-num { font-weight: 900; font-size: 28px; color: var(--pink); line-height: 1; margin-bottom: 4px; }
        .md-stat-label { font-size: 12px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500; line-height: 1.3; }

        .md-grid {
          max-width: 1080px; margin: 0 auto; padding: 0 24px 60px;
          display: grid; grid-template-columns: 1fr 420px; gap: 40px; align-items: start;
        }
        .md-info-card, .md-form-card {
          background: white; border: 1px solid var(--border); border-radius: 24px; padding: 32px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.06);
        }
        .md-form-card { position: sticky; top: 100px; }
        .md-section-label { font-size: 11px; font-weight: 700; color: var(--pink); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px; }
        .md-card-heading { font-weight: 800; font-size: 22px; color: var(--text); margin-bottom: 24px; line-height: 1.2; }
        .md-how-item { display: flex; gap: 16px; background: var(--bg); padding: 20px; border-radius: 16px; border: 1px solid var(--border); margin-bottom: 16px; }
        .md-how-num { width: 32px; height: 32px; flex-shrink: 0; background: var(--pink-light); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; color: var(--pink); }
        .md-how-title { font-weight: 700; font-size: 15px; color: var(--text); margin-bottom: 4px; }
        .md-how-text { font-size: 14px; color: var(--muted); line-height: 1.5; }
        .md-perks-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .md-perk { display: flex; align-items: center; gap: 10px; background: var(--bg); border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--text); }

        .md-form-sub { font-size: 14px; color: var(--muted); margin-bottom: 24px; line-height: 1.5; }
        .md-field-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
        .md-field-input { width: 100%; padding: 14px; border: 1.5px solid var(--border); border-radius: 12px; font-size: 15px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; margin-bottom: 20px; background: #f8fafc; transition: all 0.2s; box-sizing: border-box; }
        .md-field-input:focus { border-color: var(--pink); background: white; }
        .md-gdpr-box { background: #f8fafc; border: 1px solid var(--border); border-radius: 12px; padding: 14px; margin-bottom: 20px; }
        .md-gdpr-label { display: flex; gap: 10px; cursor: pointer; }
        .md-gdpr-checkbox { width: 16px; height: 16px; flex-shrink: 0; margin-top: 3px; accent-color: var(--pink); }
        .md-gdpr-text { font-size: 12px; color: var(--muted); line-height: 1.5; }
        .md-gdpr-text a { color: var(--pink); }
        .md-submit-btn { width: 100%; background: linear-gradient(135deg, var(--pink), var(--pink-dark)); color: white; font-weight: 800; font-size: 15px; padding: 16px; border: none; border-radius: 12px; cursor: pointer; box-shadow: 0 4px 14px rgba(219,39,119,0.3); transition: transform 0.15s; font-family: 'Sora', sans-serif; }
        .md-submit-btn:hover { transform: translateY(-1px); }
        .md-amazon-badge { background: var(--gold-bg); border: 1px solid #fde68a; color: #92400e; font-size: 12px; padding: 12px; border-radius: 10px; margin-top: 16px; text-align: center; }
        .md-testimonial { margin-top: 24px; background: var(--pink-light); border: 1px solid var(--pink-border); border-radius: 12px; padding: 16px; }

        /* APP PROMO BANNER — between form and table */
        .md-app-promo {
          max-width: 1080px; margin: 0 auto; padding: 0 24px 40px;
        }
        .md-app-promo-inner {
          background: linear-gradient(135deg, #1e3a8a, #1a56db);
          border-radius: 20px; padding: 32px 40px;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 24px;
          box-shadow: 0 10px 40px rgba(26,86,219,0.2);
        }
        .md-app-promo-btn {
          background: white; color: var(--blue-dark); font-family: 'Sora', sans-serif;
          font-weight: 800; font-size: 14px; padding: 14px 28px;
          border-radius: 12px; text-decoration: none; white-space: nowrap;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15); transition: transform 0.15s;
          display: inline-block;
        }
        .md-app-promo-btn:hover { transform: translateY(-1px); }
        .md-app-promo-btn-outline {
          background: transparent; border: 2px solid rgba(255,255,255,0.4);
          color: white; font-family: 'Sora', sans-serif; font-weight: 700;
          font-size: 13px; padding: 12px 24px; border-radius: 12px;
          text-decoration: none; white-space: nowrap; display: inline-block;
          transition: background 0.15s;
        }
        .md-app-promo-btn-outline:hover { background: rgba(255,255,255,0.1); }

        /* POPUP */
        .md-popup-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.6);
          z-index: 1000; display: flex; align-items: center; justify-content: center;
          padding: 16px;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .md-popup {
          background: white; border-radius: 24px; padding: 40px;
          max-width: 520px; width: 100%;
          box-shadow: 0 25px 60px rgba(0,0,0,0.3);
          position: relative;
          animation: slideUp 0.25s ease;
        }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .md-popup-close {
          position: absolute; top: 16px; right: 16px;
          background: #f1f5f9; border: none; border-radius: 50%;
          width: 32px; height: 32px; cursor: pointer; font-size: 16px;
          display: flex; align-items: center; justify-content: center; color: #64748b;
        }
        .md-popup-close:hover { background: #e2e8f0; }
        .md-popup-feature { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px; }
        .md-popup-feature-icon { font-size: 20px; flex-shrink: 0; margin-top: 2px; }
        .md-popup-btn-primary { display: block; width: 100%; background: linear-gradient(135deg, var(--blue), var(--blue-dark)); color: white; font-family: 'Sora', sans-serif; font-weight: 800; font-size: 15px; padding: 16px; border: none; border-radius: 12px; cursor: pointer; box-shadow: 0 4px 14px rgba(26,86,219,0.3); text-align: center; text-decoration: none; margin-bottom: 10px; transition: transform 0.15s; }
        .md-popup-btn-primary:hover { transform: translateY(-1px); }
        .md-popup-btn-secondary { display: block; width: 100%; background: white; border: 2px solid var(--border); color: var(--text); font-family: 'Sora', sans-serif; font-weight: 700; font-size: 14px; padding: 14px; border-radius: 12px; cursor: pointer; text-align: center; text-decoration: none; }

        /* PREVIEW TABLE */
        .md-preview-row { display: grid; grid-template-columns: 32px 1fr 100px 90px 110px; gap: 0; padding: 13px 32px; border-bottom: 1px solid #f1f5f9; align-items: center; }

        /* RESPONSIVE */
        @media (max-width: 850px) {
          .md-grid { grid-template-columns: 1fr; }
          .md-form-card { position: static; order: -1; }
          .md-stats { grid-template-columns: 1fr; }
          .md-perks-grid { grid-template-columns: 1fr; }
          .md-title { font-size: 30px; }
          .md-preview-row { grid-template-columns: 24px 1fr 80px; padding: 12px 16px; }
          .md-hide-mobile { display: none; }
          .md-nav { padding: 12px 16px; }
          .md-app-promo-inner { padding: 24px; flex-direction: column; text-align: center; }
          .md-popup { padding: 28px 20px; }
        }
      `}</style>

      {/* POPUP */}
      {showPopup && (
        <div className="md-popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="md-popup" onClick={(e) => e.stopPropagation()}>
            <button className="md-popup-close" onClick={() => setShowPopup(false)}>✕</button>

            <div style={{textAlign: 'center', marginBottom: '24px'}}>
              <div style={{fontSize: '36px', marginBottom: '8px'}}>🚀</div>
              <div className="sora" style={{fontWeight: 900, fontSize: '22px', color: '#0f172a', marginBottom: '8px'}}>
                Did you know our app can do all of this?
              </div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6}}>
                The keyword report is just a taster. The full app gives you the complete toolkit.
              </div>
            </div>

            <div className="md-popup-feature">
              <div className="md-popup-feature-icon">🔍</div>
              <div>
                <div style={{fontWeight: 700, fontSize: '14px', color: '#0f172a'}}>Search 75,000+ keywords instantly</div>
                <div style={{fontSize: '13px', color: '#64748b'}}>See Golden Score, competition and popularity for any TPT keyword in seconds.</div>
              </div>
            </div>
            <div className="md-popup-feature">
              <div className="md-popup-feature-icon">📈</div>
              <div>
                <div style={{fontWeight: 700, fontSize: '14px', color: '#0f172a'}}>Track exactly where your products rank</div>
                <div style={{fontSize: '13px', color: '#64748b'}}>Watch your rankings climb or drop over time and react before it affects your sales.</div>
              </div>
            </div>
            <div className="md-popup-feature">
              <div className="md-popup-feature-icon">🏆</div>
              <div>
                <div style={{fontWeight: 700, fontSize: '14px', color: '#0f172a'}}>Spy on your competitors</div>
                <div style={{fontSize: '13px', color: '#64748b'}}>See what keywords the top-ranking products are using and reverse-engineer their success.</div>
              </div>
            </div>
            <div className="md-popup-feature">
              <div className="md-popup-feature-icon">✨</div>
              <div>
                <div style={{fontWeight: 700, fontSize: '14px', color: '#0f172a'}}>Optimise every listing in seconds</div>
                <div style={{fontSize: '13px', color: '#64748b'}}>Import any TPT product and get a rewritten, SEO-ready description ready to paste.</div>
              </div>
            </div>

            <div style={{background: '#fdf2f8', border: '1px solid #f9a8d4', borderRadius: '12px', padding: '14px', marginBottom: '20px', marginTop: '8px', textAlign: 'center'}}>
              <div style={{fontSize: '13px', fontWeight: 700, color: '#9d174d', marginBottom: '4px'}}>💳 No subscriptions. Ever.</div>
              <div style={{fontSize: '12px', color: '#be185d'}}>One payment. Lifetime access. Yours forever — no monthly fees.</div>
            </div>

            <Link to="/app" className="md-popup-btn-primary">
              🚀 Try Free for 1 Hour — No Card Required
            </Link>
            <a
              href="https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Descriptions-SEO-Ranking-13645689"
              target="_blank" rel="noopener noreferrer"
              className="md-popup-btn-secondary"
            >
              Or Get Lifetime Access on TPT →
            </a>

            <div style={{textAlign: 'center', marginTop: '10px', fontSize: '11px', color: '#94a3b8'}}>
              140+ five-star reviews · 400+ active sellers · No subscription ever
            </div>
          </div>
        </div>
      )}

      {/* URGENCY BANNER */}
      <div className="md-urgency">
        🌸 Mother's Day {daysAway} — Analysed April 26th 2026 · Data from 75,000+ TPT searches
      </div>

      {/* NAV */}
      <nav className="md-nav">
        <Link to="/" className="md-nav-logo">
          <div className="md-nav-mark">MJ</div>
          <div>
            <div className="md-nav-text">Mr Joel's Seller Tools</div>
            <div className="md-nav-sub">TPT Keyword Intelligence</div>
          </div>
        </Link>
        <Link to="/app" className="md-nav-cta">Try the App Free →</Link>
      </nav>

      {/* HERO */}
      <div className="md-hero">
        <div className="md-badge">
          <div className="md-badge-dot"></div>
          Analysed April 26th 2026 · Free Keyword Report
        </div>
        <h1 className="md-title sora">
          Best TPT Keywords for<br />
          <span className="pink-accent">Mother's Day 2026</span><br />
          <span className="gold">— Ranked by Opportunity</span>
        </h1>
        <p className="md-sub">
          Mother's Day {daysAway}. We just analysed <strong>75,000+ TPT searches</strong> to find
          the top keyword opportunities before the seasonal peak hits.
          Get the full ranked report — <strong>free, direct to your inbox.</strong>
        </p>
      </div>

      {/* MAIN 2-COL LAYOUT */}
      <div className="md-grid" style={{alignItems: 'stretch'}}>

        {/* LEFT — Info Card */}
        <div className="md-info-card" style={{height: '100%', boxSizing: 'border-box'}}>
          <div className="md-section-label">What You're Getting</div>
          <h2 className="md-card-heading sora">Fresh data. Real opportunity.</h2>

          <p style={{fontSize: '15px', color: '#475569', lineHeight: 1.7, marginBottom: '24px'}}>
            We analysed <strong style={{color: '#0f172a'}}>75,000+ TPT searches</strong> on April 26th 2026 to find the best Mother's Day keyword opportunities — ranked by our Golden Score formula before the seasonal peak hits.
          </p>

          <div className="md-perks-grid" style={{marginBottom: '24px'}}>
            <div className="md-perk"><span style={{color: '#db2777', fontSize: '16px'}}>⭐</span> Golden Score Rankings</div>
            <div className="md-perk"><span style={{color: '#1a56db', fontSize: '16px'}}>📈</span> Popularity Data</div>
            <div className="md-perk"><span style={{color: '#f59e0b', fontSize: '16px'}}>🏆</span> Competition Numbers</div>
            <div className="md-perk"><span style={{color: '#8b5cf6', fontSize: '16px'}}>📅</span> Seasonal Timing Tips</div>
            <div className="md-perk"><span style={{color: '#ec4899', fontSize: '16px'}}>💡</span> Hidden Gem Keywords</div>
            <div className="md-perk"><span style={{color: '#06b6d4', fontSize: '16px'}}>🎁</span> $25 Amazon Draw Entry</div>
          </div>

          <div style={{background: '#fdf2f8', border: '1px solid #f9a8d4', borderRadius: '12px', padding: '16px'}}>
            <div style={{fontSize: '13px', fontWeight: 700, color: '#9d174d', marginBottom: '4px'}}>💡 The best sellers act early</div>
            <div style={{fontSize: '13px', color: '#be185d', lineHeight: 1.6}}>
              TPT's algorithm rewards products with existing rank history when searches spike. Publish now — not when everyone else does.
            </div>
          </div>
        </div>

        {/* RIGHT — Sticky Form Card */}
        <div className="md-form-card" style={{height: '100%', boxSizing: 'border-box'}}>
          <div className="md-section-label">Free · No Card Needed</div>
          <h2 className="md-card-heading sora">Get the Mother's Day Report</h2>

          {/* SUCCESS: NEW */}
          {submitState === 'success-new' && (
            <div style={{textAlign: 'center', padding: '16px 0'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>🌸</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>You're in!</div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px'}}>
                The Mother's Day keyword report is on its way to <strong style={{color: '#0f172a'}}>{email}</strong>.<br/>
                You're also entered into the monthly $25 Amazon draw. 🎁
              </div>
              <div style={{background: '#fdf2f8', border: '1px solid #f9a8d4', borderRadius: '12px', padding: '16px', fontSize: '13px', color: '#9d174d', marginBottom: '16px'}}>
                <strong>While you wait —</strong> try the full app free for 1 hour and search any keyword yourself.
              </div>
              <Link to="/app" className="md-submit-btn sora" style={{display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '50px', padding: '0 16px'}}>
                Try the App Free →
              </Link>
            </div>
          )}

          {/* SUCCESS: EXISTING — warm, reassuring message */}
          {submitState === 'success-existing' && (
            <div style={{textAlign: 'center', padding: '16px 0'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>👋</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>We recognise you!</div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px'}}>
                Looks like <strong style={{color: '#0f172a'}}>{email}</strong> has signed up with us before — don't worry, you're all set. 🌸<br/><br/>
                <strong>Check your inbox</strong> — the Mother's Day keyword report is on its way to you right now!
              </div>
              <div style={{background: '#fdf2f8', border: '1px solid #f9a8d4', borderRadius: '12px', padding: '16px', fontSize: '13px', color: '#9d174d', marginBottom: '16px'}}>
                Can't find it? Check your spam folder or try searching for <strong>"Mr Joel"</strong> in your inbox.
              </div>
              <Link to="/app" className="md-submit-btn sora" style={{display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '50px', padding: '0 16px'}}>
                Go to the App →
              </Link>
            </div>
          )}

          {/* FORM */}
          {(submitState === 'idle' || submitState === 'loading' || submitState === 'error') && (
            <>
              <div className="md-form-sub">
                Get the full 20-keyword report — free, no card needed, unsubscribe any time.
              </div>
              <form onSubmit={handleFormSubmit}>
                <label className="md-field-label" htmlFor="md-email">Email Address *</label>
                <input
                  className="md-field-input" type="email" id="md-email"
                  placeholder="yourname@email.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required disabled={submitState === 'loading'}
                />
                <div className="md-gdpr-box">
                  <label className="md-gdpr-label">
                    <input type="checkbox" className="md-gdpr-checkbox" checked={gdprChecked}
                      onChange={(e) => setGdprChecked(e.target.checked)}
                      required disabled={submitState === 'loading'} />
                    <span className="md-gdpr-text">
                      <strong>Yes, send me the Mother's Day keyword report.</strong><br />
                      Unsubscribe any time. We use Mailchimp. <a href="https://mailchimp.com/legal/terms" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
                    </span>
                  </label>
                </div>
                {submitState === 'error' && (
                  <div style={{background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#dc2626'}}>
                    {errorMsg}
                  </div>
                )}
                <button type="submit" className="md-submit-btn"
                  disabled={submitState === 'loading' || !gdprChecked}
                  style={{opacity: submitState === 'loading' ? 0.7 : 1, cursor: submitState === 'loading' ? 'not-allowed' : 'pointer'}}>
                  {submitState === 'loading' ? 'Sending...' : 'Send Me the Report →'}
                </button>
                <div className="md-amazon-badge">
                  🎁 <strong>Automatic Entry:</strong> Monthly $25 Amazon voucher draw
                </div>
                <div style={{textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#94a3b8'}}>
                  🔒 No spam · Free forever · One email with your report
                </div>
              </form>
            </>
          )}

        </div>
      </div>

      {/* PREVIEW TABLE */}
      <div style={{maxWidth: '1080px', margin: '0 auto', padding: '0 24px 60px'}}>
        <div style={{background: 'white', border: '1px solid #e2e8f0', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.06)'}}>

          {/* Preview Header */}
          <div style={{background: 'linear-gradient(135deg, #9d174d, #db2777)', padding: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px'}}>
            <div>
              <div style={{fontSize: '11px', fontWeight: 700, color: '#fbcfe8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px'}}>🌸 Sneak Peek — Mother's Day 2026 Report</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '24px', color: 'white'}}>Top Mother's Day keywords right now</div>
              <div style={{fontSize: '14px', color: '#fbcfe8', marginTop: '6px'}}>Analysed April 26th 2026 · Sign up above to get all 20 free</div>
            </div>
            <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
              <div style={{textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: '12px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '24px', color: '#fbbf24'}}>★ 75</div>
                <div style={{fontSize: '11px', color: '#fbcfe8', textTransform: 'uppercase', marginTop: '4px'}}>Top score</div>
              </div>
              <div style={{textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: '12px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '24px', color: '#4ade80'}}>20</div>
                <div style={{fontSize: '11px', color: '#fbcfe8', textTransform: 'uppercase', marginTop: '4px'}}>Opportunities</div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div style={{background: '#f8fafc', padding: '16px 32px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'}}>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em'}}>Score guide:</div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span className="sora" style={{background: '#fdf2f8', border: '1px solid #db2777', color: '#9d174d', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ 70+</span>
              <span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Gold Mine</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span className="sora" style={{background: '#eff6ff', border: '1px solid #1a56db', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ 20–69</span>
              <span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Strong</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span className="sora" style={{background: 'white', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ &lt;20</span>
              <span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Moderate</span>
            </div>
          </div>

          {/* Column Headers */}
          <div style={{display: 'grid', gridTemplateColumns: '32px 1fr 100px 90px 110px', gap: 0, padding: '14px 32px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0'}}>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em'}}>#</div>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em'}}>Keyword</div>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center'}}>Score</div>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'right'}}>Popularity</div>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'right'}}>Competition</div>
          </div>

          {/* Rows */}
          <div>
            {[
              { rank: 1, kw: 'mothers day craft',        note: '🔥 Massive demand, strong seasonal push',   score: 75, pop: '6,749',  comp: '11,613', tier: 'gold'   },
              { rank: 2, kw: 'mothers day questionnaire', note: '💎 High conversion, low competition',       score: 72, pop: '5,000+', comp: '~2,000', tier: 'gold'   },
              { rank: 3, kw: 'all about my mom',          note: 'Proven best-seller format',                 score: 70, pop: '4,800+', comp: '~3,500', tier: 'gold'   },
              { rank: 4, kw: 'mothers day poem',          note: 'Strong emotional + printable niche',        score: 65, pop: '4,200+', comp: '~6,000', tier: 'strong' },
              { rank: 5, kw: 'mothers day writing',       note: 'Great classroom activity keyword',          score: 60, pop: '3,800+', comp: '~4,500', tier: 'strong' },
            ].map(({ rank, kw, note, score, pop, comp, tier }) => (
              <div key={rank} className="md-preview-row" style={{background: rank % 2 === 0 ? '#fafafa' : 'white'}}>
                <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>{rank}</div>
                <div>
                  <div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>{kw}</div>
                  <div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>{note}</div>
                </div>
                <div style={{textAlign: 'center'}}>
                  <span className="sora" style={{
                    background: tier === 'gold' ? '#fdf2f8' : '#eff6ff',
                    border: `1px solid ${tier === 'gold' ? '#db2777' : '#1a56db'}`,
                    color: tier === 'gold' ? '#9d174d' : '#1d4ed8',
                    fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap'
                  }}>★ {score}</span>
                </div>
                <div className="md-hide-mobile" style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>{pop}</div>
                <div className="md-hide-mobile" style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>{comp}</div>
              </div>
            ))}

            {/* Blurred rows */}
            <div style={{position: 'relative'}}>
              <div style={{filter: 'blur(5px)', pointerEvents: 'none', opacity: 0.6}}>
                {[
                  { rank: 6, kw: 'mothers day writing prompts',     note: 'Low competition sweet spot', score: 58, pop: '2,700+', comp: '~2,800' },
                  { rank: 7, kw: 'mothers day reading comprehension',note: 'Underserved niche',          score: 55, pop: '2,400+', comp: '~2,200' },
                  { rank: 8, kw: 'mothers day bookmark',            note: '💎 Very low competition',     score: 48, pop: '1,600+', comp: '~480'   },
                ].map(({ rank, kw, note, score, pop, comp }) => (
                  <div key={rank} className="md-preview-row" style={{borderBottom: rank === 8 ? 'none' : undefined}}>
                    <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>{rank}</div>
                    <div>
                      <div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>{kw}</div>
                      <div style={{fontSize: '12px', color: '#64748b'}}>{note}</div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                      <span className="sora" style={{background: 'white', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px'}}>★ {score}</span>
                    </div>
                    <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>{pop}</div>
                    <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>{comp}</div>
                  </div>
                ))}
              </div>
              <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, rgba(248,250,252,0) 0%, rgba(248,250,252,0.9) 40%, rgba(248,250,252,1) 100%)'}}>
                <div style={{textAlign: 'center', padding: '30px', background: 'white', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0'}}>
                  <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>+ 15 more keywords in the full report</div>
                  <div style={{fontSize: '14px', color: '#64748b', marginBottom: '20px'}}>Sign up free to get all 20 sent directly to your inbox</div>
                  <a href="#md-email" onClick={scrollToForm} className="sora" style={{background: 'linear-gradient(135deg, #db2777, #9d174d)', color: 'white', fontWeight: 800, fontSize: '15px', padding: '14px 32px', borderRadius: '12px', textDecoration: 'none', display: 'inline-block', boxShadow: '0 4px 16px rgba(219,39,119,0.3)'}}>
                    Get the Full Report Free →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* APP PROMO BANNER — compact single row */}
      <div className="md-app-promo">
        <div style={{
          background: 'linear-gradient(135deg, #1e3a8a, #1a56db)',
          borderRadius: '16px',
          padding: '20px 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          flexWrap: 'wrap',
          boxShadow: '0 10px 40px rgba(26,86,219,0.2)',
        }}>
          {/* Left: label + headline */}
          <div style={{display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: '260px'}}>
            <div>
              <div style={{fontSize: '11px', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px'}}>💡 Did you know?</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '17px', color: 'white', lineHeight: 1.3}}>
                Our app lets you search all 75,000+ keywords — rank tracking, competitor analysis & listing optimisation in one place.
              </div>
            </div>
          </div>

          {/* Middle: 3 stats */}
          <div style={{display: 'flex', gap: '8px', flexShrink: 0}}>
            {[['20', 'Keywords'], ['75k+', 'Analysed'], ['140+', '⭐ Reviews']].map(([num, label]) => (
              <div key={label} style={{textAlign: 'center', background: 'rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px 14px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '20px', color: '#fbbf24', lineHeight: 1}}>{num}</div>
                <div style={{fontSize: '10px', color: '#93c5fd', marginTop: '3px', textTransform: 'uppercase', whiteSpace: 'nowrap'}}>{label}</div>
              </div>
            ))}
          </div>

          {/* Right: CTAs */}
          <div style={{display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap'}}>
            <Link to="/app" className="md-app-promo-btn" style={{fontSize: '13px', padding: '10px 20px'}}>🚀 Try Free →</Link>
            <button onClick={() => setShowPopup(true)} className="md-app-promo-btn-outline" style={{fontSize: '12px', padding: '10px 16px'}}>See Features</button>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{background: 'white', borderTop: '1px solid var(--border)', textAlign: 'center', padding: '32px 24px', fontSize: '13px', color: 'var(--muted)'}}>
        <p>
          <a href="https://mrjoelstptgenerator.com" style={{color: 'var(--pink)', textDecoration: 'none'}}>mrjoelstptgenerator.com</a> &nbsp;·&nbsp;
          <Link to="/app" style={{color: 'var(--pink)', textDecoration: 'none'}}>Try the App Free</Link> &nbsp;·&nbsp;
          <a href="https://www.teacherspayteachers.com/store/mr-joels-math-science-store" style={{color: 'var(--pink)', textDecoration: 'none'}}>My TPT Store</a> &nbsp;·&nbsp;
          <a href="https://www.facebook.com/profile.php?id=61577123933005" style={{color: 'var(--pink)', textDecoration: 'none'}}>Facebook</a>
        </p>
        <p style={{marginTop: '8px'}}>© 2026 Mr Joel's Seller Tools · Data analysed April 26th 2026 · Not affiliated with Teachers Pay Teachers.</p>
      </footer>

    </div>
  );
};

export default MothersDay2026;